import { supabase } from '../supabase'
import { getOrCreateActiveScheme } from './rfidIdentifierService'
import { decodeEpc, type DecodeEpcFields, type DecodeEpcResult } from '../../utils/epcComposer'
import type { InventoryItem } from '../../types'

export type RfidUnitStatus = 'PENDING' | 'ENCODED' | 'QUARANTINED' | 'SUPERSEDED' | 'ISSUED'

export interface RfidScanResolveResult {
  kind: 'epc'
  epcFull: string
  epcBase: string
  serial: string
  decoded: DecodeEpcFields
  productId: string | null
  sku: string | null
  barcode: string | null
  item: InventoryItem | null
  unitFound: boolean
  unitStatus: RfidUnitStatus | null
  integrityAlarm: boolean
  message: string | null
  success: boolean
}

/** ENCODED and ISSUED (Zebra print file, not reader-verified) rows describe a live unit. */
const DEAD_UNIT_MESSAGES: Record<Exclude<RfidUnitStatus, 'ENCODED' | 'ISSUED'>, string> = {
  SUPERSEDED: 'This tag was retired and re-encoded. Scan the current tag.',
  QUARANTINED: "This tag's contents are unverified. Set it aside.",
  PENDING: 'Encoding of this tag never completed.',
}

function mapProductRow(p: Record<string, unknown>): InventoryItem {
  return {
    id: String(p.id),
    user_id: String(p.admin_id),
    name: String(p.name ?? ''),
    description: (p.description as string) || undefined,
    sku: (p.sku as string) || undefined,
    barcode: (p.barcode as string) || undefined,
    batch: ((p.barcode as string) || (p.sku as string)) || undefined,
    batch_number: (p.batch_number as string) || undefined,
    reference_code: (p.reference_code as string) || undefined,
    category: String(p.category ?? ''),
    quantity: Number(p.quantity) || 0,
    unit_price: Number(p.unit_price ?? p.sell_price) || 0,
    buy_price: Number(p.purchase_price ?? p.cost_price) || undefined,
    sell_price: Number(p.sell_price ?? p.unit_price) || undefined,
    color_id: (p.color_id as string) || undefined,
    size_id: (p.size_id as string) || undefined,
    image_url: (p.image_url as string) || undefined,
    created_at: String(p.created_at ?? ''),
    updated_at: String(p.updated_at ?? ''),
  }
}

async function loadProductById(
  adminId: string,
  productId: string
): Promise<InventoryItem | null> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('admin_id', adminId)
    .eq('id', productId)
    .maybeSingle()

  if (error) throw new Error(error.message || 'Failed to load product for EPC unit')
  if (!data) return null
  return mapProductRow(data)
}

/**
 * The 96-bit scheme carries the style code, not the item code — style already
 * implies the item (`UNIQUE (admin_id, style_code)`). The item-code lookup stays
 * as a fallback for schemes that still carry an item segment.
 */
async function lookupItemIdentifier(
  adminId: string,
  fields: DecodeEpcFields
): Promise<{ itemName: string | null; message: string | null }> {
  if (fields.styleCode) {
    const { data, error } = await supabase
      .from('rfid_item_identifiers')
      .select('item_name')
      .eq('admin_id', adminId)
      .eq('style_code', fields.styleCode)
      .maybeSingle()

    if (error) throw new Error(error.message || 'Failed to resolve style identifier')
    if (data) return { itemName: data.item_name, message: null }
    return { itemName: null, message: `No item identifier for style ${fields.styleCode}` }
  }

  if (fields.itemCode) {
    const { data, error } = await supabase
      .from('rfid_item_identifiers')
      .select('item_name')
      .eq('admin_id', adminId)
      .eq('item_code', fields.itemCode)
      .maybeSingle()

    if (error) throw new Error(error.message || 'Failed to resolve item identifier')
    if (data) return { itemName: data.item_name, message: null }
    return { itemName: null, message: `No item identifier for code ${fields.itemCode}` }
  }

  return { itemName: null, message: 'EPC carries neither a style code nor an item code' }
}

async function resolveVariantFromDecoded(
  adminId: string,
  fields: DecodeEpcFields
): Promise<{ item: InventoryItem | null; message: string | null }> {
  const { itemName, message: identMessage } = await lookupItemIdentifier(adminId, fields)
  if (!itemName) return { item: null, message: identMessage }
  const itemIdent = { item_name: itemName }

  let colorId: string | null = null
  if (fields.colorCode) {
    const { data: colorIdent, error: colorErr } = await supabase
      .from('rfid_color_identifiers')
      .select('color_id, code')
      .eq('admin_id', adminId)
      .eq('code', fields.colorCode)
      .maybeSingle()
    if (colorErr) throw new Error(colorErr.message || 'Failed to resolve color identifier')
    if (!colorIdent)
      return { item: null, message: `No color identifier for code ${fields.colorCode}` }
    colorId = colorIdent.color_id
  }

  let sizeId: string | null = null
  if (fields.sizeCode) {
    const { data: sizeIdent, error: sizeErr } = await supabase
      .from('rfid_size_identifiers')
      .select('size_id, code')
      .eq('admin_id', adminId)
      .eq('code', fields.sizeCode)
      .maybeSingle()
    if (sizeErr) throw new Error(sizeErr.message || 'Failed to resolve size identifier')
    if (!sizeIdent)
      return { item: null, message: `No size identifier for code ${fields.sizeCode}` }
    sizeId = sizeIdent.size_id
  }

  let query = supabase
    .from('products')
    .select('*')
    .eq('admin_id', adminId)
    .eq('name', itemIdent.item_name)

  if (colorId) query = query.eq('color_id', colorId)
  else query = query.is('color_id', null)

  if (sizeId) query = query.eq('size_id', sizeId)
  else query = query.is('size_id', null)

  const { data: products, error: productErr } = await query.limit(2)
  if (productErr) throw new Error(productErr.message || 'Failed to resolve product from EPC base')

  if (!products || products.length === 0) {
    return {
      item: null,
      message: `No product variant for ${itemIdent.item_name} (color ${fields.colorCode || '—'} / size ${fields.sizeCode || '—'})`,
    }
  }

  if (products.length > 1) {
    return {
      item: mapProductRow(products[0]),
      message: `Multiple variants matched EPC base for ${itemIdent.item_name}; using first match`,
    }
  }

  return { item: mapProductRow(products[0]), message: null }
}

async function expectedCodesForProduct(
  adminId: string,
  item: InventoryItem
): Promise<{
  itemCode: string | null
  styleCode: string | null
  colorCode: string | null
  sizeCode: string | null
}> {
  const { data: itemIdent } = await supabase
    .from('rfid_item_identifiers')
    .select('item_code, style_code')
    .eq('admin_id', adminId)
    .eq('item_name', item.name)
    .maybeSingle()

  let colorCode: string | null = null
  if (item.color_id) {
    const { data } = await supabase
      .from('rfid_color_identifiers')
      .select('code')
      .eq('admin_id', adminId)
      .eq('color_id', item.color_id)
      .maybeSingle()
    colorCode = data?.code ?? null
  }

  let sizeCode: string | null = null
  if (item.size_id) {
    const { data } = await supabase
      .from('rfid_size_identifiers')
      .select('code')
      .eq('admin_id', adminId)
      .eq('size_id', item.size_id)
      .maybeSingle()
    sizeCode = data?.code ?? null
  }

  return {
    itemCode: itemIdent?.item_code ?? null,
    styleCode: itemIdent?.style_code ?? null,
    colorCode,
    sizeCode,
  }
}

function buildIntegrityAlarm(
  decoded: DecodeEpcFields,
  expected: {
    itemCode: string | null
    styleCode: string | null
    colorCode: string | null
    sizeCode: string | null
  },
  productName: string
): string | null {
  const mismatches: string[] = []
  if (expected.styleCode && decoded.styleCode && expected.styleCode !== decoded.styleCode)
    mismatches.push(`style ${expected.styleCode} vs EPC ${decoded.styleCode}`)
  if (expected.itemCode && decoded.itemCode && expected.itemCode !== decoded.itemCode)
    mismatches.push(`item ${expected.itemCode} vs EPC ${decoded.itemCode}`)
  if (expected.colorCode && decoded.colorCode && expected.colorCode !== decoded.colorCode)
    mismatches.push(`color ${expected.colorCode} vs EPC ${decoded.colorCode}`)
  if (expected.sizeCode && decoded.sizeCode && expected.sizeCode !== decoded.sizeCode)
    mismatches.push(`size ${expected.sizeCode} vs EPC ${decoded.sizeCode}`)

  if (mismatches.length === 0) return null
  return `DATA INTEGRITY ALARM for ${productName}: ${mismatches.join('; ')}. Unit record and EPC disagree.`
}

export async function resolveEpcScan(
  adminId: string,
  epcRaw: string
): Promise<RfidScanResolveResult> {
  if (!adminId) throw new Error('Admin ID is required')

  const scheme = await getOrCreateActiveScheme(adminId)
  const decoded: DecodeEpcResult = decodeEpc(scheme, epcRaw)

  if (!decoded.isValid) {
    return {
      kind: 'epc',
      epcFull: decoded.epcFull,
      epcBase: decoded.epcBase,
      serial: decoded.serial,
      decoded: decoded.fields,
      productId: null,
      sku: null,
      barcode: null,
      item: null,
      unitFound: false,
      unitStatus: null,
      integrityAlarm: false,
      message: decoded.errors.join('; '),
      success: false,
    }
  }

  const { data: unitRow, error: unitErr } = await supabase
    .from('rfid_encoded_units')
    .select('id, product_id, epc_full, epc_base, serial, status')
    .eq('admin_id', adminId)
    .eq('epc_full', decoded.epcFull)
    .maybeSingle()

  if (unitErr) throw new Error(unitErr.message || 'Failed to look up encoded unit')

  if (unitRow) {
    const unitStatus = (unitRow.status as RfidUnitStatus) || 'ENCODED'
    const item = await loadProductById(adminId, unitRow.product_id)

    if (!item) {
      return {
        kind: 'epc',
        epcFull: decoded.epcFull,
        epcBase: decoded.epcBase,
        serial: decoded.serial,
        decoded: decoded.fields,
        productId: unitRow.product_id,
        sku: null,
        barcode: null,
        item: null,
        unitFound: true,
        unitStatus,
        integrityAlarm: true,
        message: `Unit record points to missing product ${unitRow.product_id}`,
        success: false,
      }
    }

    if (unitStatus !== 'ENCODED' && unitStatus !== 'ISSUED') {
      return {
        kind: 'epc',
        epcFull: decoded.epcFull,
        epcBase: decoded.epcBase,
        serial: decoded.serial,
        decoded: decoded.fields,
        productId: item.id,
        sku: item.sku || null,
        barcode: item.barcode || null,
        item,
        unitFound: true,
        unitStatus,
        integrityAlarm: false,
        message: DEAD_UNIT_MESSAGES[unitStatus],
        success: false,
      }
    }

    const expected = await expectedCodesForProduct(adminId, item)
    const integrityMessage = buildIntegrityAlarm(decoded.fields, expected, item.name)

    return {
      kind: 'epc',
      epcFull: decoded.epcFull,
      epcBase: decoded.epcBase,
      serial: decoded.serial,
      decoded: decoded.fields,
      productId: item.id,
      sku: item.sku || null,
      barcode: item.barcode || null,
      item,
      unitFound: true,
      unitStatus,
      integrityAlarm: !!integrityMessage,
      message: integrityMessage,
      success: true,
    }
  }

  // Unit miss — fall back to EPC base → variant
  const { item, message } = await resolveVariantFromDecoded(adminId, decoded.fields)

  if (!item) {
    return {
      kind: 'epc',
      epcFull: decoded.epcFull,
      epcBase: decoded.epcBase,
      serial: decoded.serial,
      decoded: decoded.fields,
      productId: null,
      sku: null,
      barcode: null,
      item: null,
      unitFound: false,
      unitStatus: null,
      integrityAlarm: false,
      message: message || 'Could not resolve product from EPC base',
      success: false,
    }
  }

  return {
    kind: 'epc',
    epcFull: decoded.epcFull,
    epcBase: decoded.epcBase,
    serial: decoded.serial,
    decoded: decoded.fields,
    productId: item.id,
    sku: item.sku || null,
    barcode: item.barcode || null,
    item,
    unitFound: false,
    unitStatus: null,
    integrityAlarm: false,
    message:
      'No unit record for this EPC. Tag may have been encoded outside the system, or the unit record was lost. Variant resolved from EPC base.',
    success: true,
  }
}
